import type { AppRouter } from "../router";
import type { CommercialView, TabContext } from "./tab-context";

// Cambiare l'azienda della scheda e uscire sono navigazioni verso due rotte tecniche (piano M1b D2): prima
// si naviga, così una bozza non salvata può fermare il cambio con il suo dialog (`useBlocker`) e, se la
// persona resta, nulla è cambiato. Il lavoro vero sta nel `beforeLoad` delle due rotte (`router.tsx`).
// La Vista Commerciale (piano M2 Task 22) cambia allo stesso modo, con una terza rotta tecnica.

export const SWITCH_TAB_PATH = "/cambia-azienda";
export const SIGN_OUT_PATH = "/esci";
export const CHANGE_VIEW_PATH = "/cambia-vista";

/** Le rotte tecniche: nessuna serve come ritorno dopo l'accesso o dopo un cambio di vista. */
export const TECHNICAL_PATHS: ReadonlySet<string> = new Set([SWITCH_TAB_PATH, SIGN_OUT_PATH, CHANGE_VIEW_PATH]);

/**
 * L'azienda di destinazione viaggia nello stato della cronologia, mai nell'indirizzo: un link non la cambia. Di solito si arriva
 * alla Home dell'azienda; `returnTo`, una pagina del programma senza l'indirizzo dell'azienda (`/impostazioni/…`), riapre quella
 * pagina nell'azienda nuova: «Entra come Super admin di Gioia365» di «Ogni sera da Close» (18/10/2026) torna sulla stessa pagina.
 */
export async function switchTab(router: AppRouter, next: TabContext, returnTo?: string): Promise<void> {
  await router.navigate({ to: SWITCH_TAB_PATH, state: { nextTab: next, ...(returnTo === undefined ? {} : { switchReturn: returnTo }) } });
}

/**
 * Apre la Vista Commerciale di un Commerciale, o ne esce (`null`). La vista e la pagina di adesso, a cui si torna,
 * viaggiano nello stato della cronologia, mai nell'indirizzo: un link non apre una vista.
 */
export async function changeCommercialView(router: AppRouter, next: CommercialView | null): Promise<void> {
  await router.navigate({ to: CHANGE_VIEW_PATH, state: { nextView: next, viewReturn: router.state.location.pathname } });
}

/**
 * «Esci». Il segnale nello stato della cronologia dice che l'uscita l'ha chiesta la persona: `/esci` aperta
 * da un link, da un ritorno dopo l'accesso o scritta a mano (anche come `/esci/` o `/ESCI`, che il router
 * riconosce come `/esci`) non ce l'ha, e porta alla Home senza far uscire nessuno.
 */
export async function signOutTab(router: AppRouter): Promise<void> {
  await router.navigate({ to: SIGN_OUT_PATH, state: { signOut: true } });
}

/** L'uscita non è riuscita: la scheda resta com'era e la pagina d'errore offre «Riprova». */
export class SignOutError extends Error {
  constructor() {
    super("Sign-out failed");
    this.name = "SignOutError";
  }
}
